import { FormikProps } from 'formik';

import { Nullable, Optional } from '@common/typescript/objects/Nullable';
import {
	getShallowChanges,
	pickDifference,
} from '@common/typescript/utils/entity';

import { ServiceType, DeliveryType } from '@app/objects/Pet';
import { ViewMode } from '@app/components/Utils/Utils';
import { Clinic } from '@app/objects/Clinic';
import { PriceKind } from '@app/objects/Price';
import {
	CreateMessage,
	Delivery,
	FormValues,
	PetPriceMessage,
	UpdateMessage,
} from '@app/components/Pages/ClinicPetEditor/types';
import { ClinicCrematory } from '@app/objects/ClinicCrematory';
import { CrematoryListInfo } from '@app/objects/Crematory';
import { DeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';
import { PetPrice } from '@app/objects/PetPrice';

function toPetPriceMessage(item: PetPrice): PetPriceMessage {
	let priceKind = item.price?.priceKind;

	if (priceKind !== PriceKind.SpecialServicePrice && priceKind !== PriceKind.ProductPrice) {
		priceKind = undefined;
	}

	return {
		id: item.id,
		removed: item.removed,

		petId: item.id,
		priceId: item.priceId,
		priceKind,

		count: item.count,
		completedCount: item.completedCount,
		done: item.done,

		name: item.name,
		value: item.value,
		extra: item.extra,
		tax: item.tax,
		discount: item.discount,

		batchCount: item.batchCount,
		batchPrice: item.batchPrice,

		pickupService: item.pickupService,
		pickupServiceId: item.pickupServiceId,

		node: null,
		nodeId: item.nodeId,

		engravingId: null,
		engraving: null,

		note: item.note,
	};
}

function getDeliveryValue(delivery: Pick<Delivery, 'deliveryType' | 'deliveryTypeId'>, manager: DeliveryManager): Nullable<number> {
	const value = manager.type === 'modern' ? delivery.deliveryTypeId : delivery.deliveryType;

	return typeof value === 'undefined' ? null : value;
}

function getDelivery(values: FormValues, manager: DeliveryManager): Partial<Delivery> {
	const delivery: Partial<Delivery> = {
		deliveryType: values.deliveryType,
		deliveryTypeId: values.deliveryTypeId,
	};

	const value = getDeliveryValue(delivery, manager);

	if (manager.isClinic(value)) {
		delivery.deliveryClinicId = values.deliveryClinicId;
	}

	if (manager.isClinic(value) || manager.isMail(value) || manager.isHomeDropOff(value)) {
		delivery.deliveryAddress = values.deliveryAddress;
		delivery.deliveryAddress2 = values.deliveryAddress2;
		delivery.deliveryCity = values.deliveryCity;
		delivery.deliveryZip = values.deliveryZip;
		delivery.deliveryStateId = values.deliveryStateId;
	}

	return delivery;
}

function fillDelivery(message: UpdateMessage): UpdateMessage {
	const keys: Array<keyof UpdateMessage> = [
		'deliveryAddress',
		'deliveryAddress2',
		'deliveryCity',
		'deliveryZip',
		'deliveryStateId',
	];
	keys.forEach((key: keyof UpdateMessage) => {
		if (message[key]) return;

		// eslint-disable-next-line
		// @ts-ignore
		message[key] = undefined;
	});

	return message;
}

export function toCreateMessage(values: FormValues, manager: DeliveryManager): CreateMessage {
	const services = [
		...values.services,
		...values.products,
		...values.urns,
	]
		.filter((item: PetPrice) => !item.removed)
		.map(toPetPriceMessage);

	return {
		name: values.name,
		engraving: values.engraving ?? [],
		color: values.color,
		reportedWeight: values.reportedWeight,

		discountId: values.discountId,
		genderId: values.genderId ?? -1,
		petBreedId: values.petBreedId,
		petSpecieId: values.petSpecieId,
		isMixed: values.isMixed,
		clinicLocationId: values.clinicLocationId,
		deliveryLocationId: values.deliveryLocationId,

		...getDelivery(values, manager),

		ownerEmail: values.ownerEmail,
		ownerFirstName: values.ownerFirstName,
		ownerLastName: values.ownerLastName,
		ownerPhone: values.ownerPhone,
		ownerAddress: values.ownerAddress,
		ownerAddress2: values.ownerAddress2,
		ownerCity: values.ownerCity,
		ownerState: values.ownerState,
		ownerStateId: values.ownerStateId,
		ownerZip: values.ownerZip,

		services,
		serviceType: values.serviceType,
		specialInstructions: values.specialInstructions,

		noAshes: values.noAshes,
		onHold: values.onHold,
		rush: values.rush,
		internalIdNum: values.internalIdNum,
		files: values.files,
	};
}

function convertToUpdateMessage(values: FormValues, manager: DeliveryManager): UpdateMessage {
	const services = [
		...values.services,
		...values.products,
		...values.urns,
	].map(toPetPriceMessage);

	return {
		id: values.id,

		name: values.name,
		engraving: values.engraving ?? [],
		color: values.color,
		reportedWeight: values.reportedWeight,

		discountId: values.discountId,
		genderId: values.genderId ?? -1,
		petBreedId: values.petBreedId,
		petSpecieId: values.petSpecieId,
		isMixed: values.isMixed,
		clinicLocationId: values.clinicLocationId,
		deliveryLocationId: values.deliveryLocationId,

		...getDelivery(values, manager),

		ownerEmail: values.ownerEmail,
		ownerFirstName: values.ownerFirstName,
		ownerLastName: values.ownerLastName,
		ownerPhone: values.ownerPhone,
		ownerStateId: values.ownerStateId,
		ownerState: values.ownerState,
		ownerCity: values.ownerCity,
		ownerZip: values.ownerZip,
		ownerAddress: values.ownerAddress,
		ownerAddress2: values.ownerAddress2,

		specialInstructions: values.specialInstructions,
		serviceType: values.serviceType,
		services,

		noAshes: values.noAshes,
		onHold: values.onHold,
		rush: values.rush,
		internalIdNum: values.internalIdNum,
		files: values.files,
	};
}

export function toUpdateMessage(values: FormValues, initial: FormValues, manager: DeliveryManager): Partial<UpdateMessage> {
	const initialMessage = fillDelivery(convertToUpdateMessage(initial, manager));
	const currentMessage = convertToUpdateMessage(values, manager);
	const data = getShallowChanges(initialMessage, currentMessage);

	const newServices = (data.services ?? []).filter((q: PetPriceMessage) => q.id === -1);
	const updatedServices = (data.services ?? []).filter((q: PetPriceMessage) => q.id > 0)
		.filter((q1: PetPriceMessage) => {
			const initialServices = [...initial.services, ...initial.products, ...initial.urns];
			const value = initialServices.find((q2: PetPrice) => q2.id === q1.id);

			if (value === undefined) return false;

			return value.removed !== q1.removed
				|| value.count !== q1.count
				|| value.value !== q1.value
				|| value.priceId !== q1.priceId
				|| value.nodeId !== q1.nodeId
				|| value.note !== q1.note;
		});

	data.services = [...newServices, ...updatedServices];
	data.serviceType = currentMessage.serviceType;

	return {
		...data,
		withCrematory: true,
		withSpecie: true,
		withBreed: true,
		withGender: true,
		withServices: true,
		withUrns: true,
	};
}

const formatServices = (array: Array<PetPrice>) => {
	const filteredArray = array.filter((i) => i.price?.priceKind === PriceKind.SpecialServicePrice || i.price?.priceKind === PriceKind.UrnPrice);

	if (filteredArray) {
		return filteredArray.map((item: PetPrice) => {
			return {
				completedCount: item.completedCount,
				count: item.count,
				done: item.done,
				extra: item.extra,
				id: item.id,
				name: item.name,
				price: null,
				priceId: item.priceId,
				value: item.value,
				removed: item.removed ?? false,
			};
		});
	}

	return null;
};

export function servicesDiff(initial: Array<PetPrice>, update: Array<PetPrice>) {
	const initialValues = formatServices(initial);
	const updateValues = formatServices(update);

	if (!initialValues || !updateValues) return false;

	return pickDifference(initialValues, updateValues).length;
}

interface SetDeliveryTypeConfig {
	props: FormikProps<FormValues>;
	clinic: Nullable<Clinic>;
	crematory: Optional<CrematoryListInfo>;
	useDeliveryEntity: boolean;
}

function getDeliveryType(clinic: Clinic, serviceType: ServiceType): DeliveryType {
	switch (serviceType) {
	case ServiceType.Private: return clinic.defaultPrivateDeliveryType;
	case ServiceType.SemiPrivate: return clinic.defaultSemiPrivateDeliveryType;
	case ServiceType.Communal: return clinic.defaultCommunalDeliveryType;

	default: return clinic.defaultCommunalDeliveryType;
	}
}

function getDeliveryEntity(link: ClinicCrematory, serviceType: ServiceType): Nullable<number> {
	switch (serviceType) {
	case ServiceType.Private: return link.defaultPrivateDeliveryTypeId;
	case ServiceType.SemiPrivate: return link.defaultSemiPrivateDeliveryTypeId;
	case ServiceType.Communal: return link.defaultCommunalDeliveryTypeId;
	case ServiceType.ProductsOnly: return link.defaultProductsOnlyDeliveryTypeId;
	case ServiceType.Storage: return link.defaultStorageDeliveryTypeId;

	default: return null;
	}
}

export function setDefaultDeliveryType(config: SetDeliveryTypeConfig) {
	if (!config.clinic) return;

	const serviceType = config.props.values.serviceType ?? ServiceType.Communal;

	if (!config.useDeliveryEntity) {
		config.props.setFieldValue('deliveryType', getDeliveryType(config.clinic, serviceType), false);
	}

	if (!config.crematory) return;

	const crematoryId = config.crematory.id;
	const link = config.clinic.clinicCrematories.find((q: ClinicCrematory) => q.crematoryId === crematoryId);
	if (!link) return;

	config.props.setFieldValue('deliveryTypeId', getDeliveryEntity(link, serviceType), false);
}

export function getMode(type: string, isEditable: boolean): ViewMode {
	return type === 'edit' && isEditable ? ViewMode.edit : ViewMode.view;
}
